// src/styles/GlobalStyles.js
import { createGlobalStyle } from 'styled-components';

export const theme = {
  colors: {
    dark: '#0a0a12',
    darker: '#06060d',
    text: '#f8fafc',
    textSecondary: 'rgba(248, 250, 252, 0.68)',
    textMuted: 'rgba(248, 250, 252, 0.42)',
    border: 'rgba(255, 255, 255, 0.09)',
    borderStrong: 'rgba(255, 255, 255, 0.16)',
    glass: 'rgba(255, 255, 255, 0.04)',
    glassStrong: 'rgba(255, 255, 255, 0.07)',
    purple: '#9370DB',
    teal: '#4dd0e1',
    // accent hues used across the new UI
    indigo: '#6366f1',
    violet: '#8b5cf6',
    pink: '#ec4899',
    cyan: '#06b6d4',
    emerald: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #6366f1, #ec4899)',
    secondary: 'linear-gradient(135deg, #06b6d4, #10b981)',
    accent: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    // richer multi-stop gradients for the reinvention
    aurora: 'linear-gradient(120deg, #6366f1 0%, #8b5cf6 35%, #ec4899 70%, #f59e0b 100%)',
    holo: 'linear-gradient(135deg, #06b6d4, #6366f1, #ec4899)',
    conic: 'conic-gradient(from 180deg at 50% 50%, #6366f1, #ec4899, #f59e0b, #06b6d4, #6366f1)',
  },
  shadows: {
    sm: '0 4px 6px rgba(0, 0, 0, 0.1)',
    md: '0 10px 15px rgba(0, 0, 0, 0.2)',
    lg: '0 20px 25px rgba(0, 0, 0, 0.3)',
    glow: '0 0 40px rgba(99, 102, 241, 0.35)',
    glowPink: '0 0 40px rgba(236, 72, 153, 0.3)',
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
  transitions: {
    default: '0.3s ease',
    slow: '0.6s ease',
    fast: '0.15s ease',
    spring: '0.5s cubic-bezier(0.16, 1, 0.3, 1)',
  }
};

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    background: ${({ theme }) => theme.colors.dark};
    color: ${({ theme }) => theme.colors.text};
    line-height: 1.6;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  .container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 0 1.5rem;
    }
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 1.2;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.default};
  }
  
  button {
    font-family: inherit;
    cursor: pointer;
  }
  
  .section {
    padding: 6rem 0;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 4rem 0;
    }
  }
  
  .bg-glass {
    background: ${({ theme }) => theme.colors.glass};
    backdrop-filter: blur(16px);
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 16px;
  }
  
  /* Utility classes */
  .gradient-text-primary {
    background: ${({ theme }) => theme.gradients.primary};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
      /* Fallback solid color for mobile if gradient doesn't show well */
      color: #ec4899;
      background: none;
      -webkit-background-clip: initial;
      background-clip: initial;
    }
  }
  
  .gradient-text-secondary {
    background: ${({ theme }) => theme.gradients.secondary};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
      /* Fallback solid color for mobile if gradient doesn't show well */
      color: #06b6d4;
      background: none;
      -webkit-background-clip: initial;
      background-clip: initial;
    }
  }
  
  .gradient-text-accent {
    background: ${({ theme }) => theme.gradients.accent};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
      /* Fallback solid color for mobile if gradient doesn't show well */
      color: #f59e0b;
      background: none;
      -webkit-background-clip: initial;
      background-clip: initial;
    }
  }

  /* Gradient backgrounds */
  .gradient-bg-primary,
  .gradient-bg-secondary,
  .gradient-bg-accent {
    position: relative;
  }

  .gradient-bg-primary::after,
  .gradient-bg-secondary::after,
  .gradient-bg-accent::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 0;
    pointer-events: none;
    border-radius: inherit;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    body {
      /* Darker background gradient for mobile to improve contrast */
      background: linear-gradient(to bottom, #0c1024, #121936);
    }
    
    h1, h2, h3, h4, h5, h6 {
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
    }
    
    p {
      text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
    }
    
    .bg-glass {
      background-color: rgba(24, 28, 46, 0.85) !important; /* Darker glass background on mobile */
      backdrop-filter: blur(10px);
    }
  }
  
  /* For animations */
  .fade-in {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  
  .fade-in.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  /* Mobile text container helper class */
  .mobile-text-container {
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      background-color: rgba(10, 14, 32, 0.7);
      padding: 1rem;
      border-radius: 8px;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.05);
    }
  }

  /* ===== Reinvention: global motion + texture ===== */

  html {
    scroll-behavior: smooth;
  }

  /* Selection */
  ::selection {
    background: rgba(236, 72, 153, 0.35);
    color: #fff;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar { width: 10px; }
  ::-webkit-scrollbar-track { background: ${({ theme }) => theme.colors.darker}; }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #6366f1, #ec4899);
    border-radius: 10px;
    border: 2px solid ${({ theme }) => theme.colors.darker};
  }

  /* Fine-grained film-grain / noise overlay on the whole page */
  body::after {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 9997;
    opacity: 0.035;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* Animated gradient text (moving hue) */
  .grad-animate {
    background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #f59e0b, #06b6d4, #6366f1);
    background-size: 300% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: gradientShift 8s linear infinite;
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    100% { background-position: 300% 50%; }
  }

  @keyframes floatSlow {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(4%, -6%) scale(1.08); }
    66% { transform: translate(-5%, 3%) scale(0.96); }
  }

  @keyframes spinSlow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes pulseRing {
    0% { transform: scale(0.9); opacity: 0.7; }
    70% { transform: scale(1.6); opacity: 0; }
    100% { opacity: 0; }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  /* Marquee track */
  @keyframes marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
      scroll-behavior: auto !important;
    }
  }
`;